import ExportIcon from "@mui/icons-material/OutputRounded";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useState } from "react";
import { useSelector } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { PortalWidget } from "@entrilia/shared/api/portalWidgetsTypes";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import MultiSelectFilter from "@entrilia/shared/components/MultiSelectFilter";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import CompaniesIcon from "@entrilia/shared/icons/CompaniesIcon";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl, downloadFileFromUrlViaAxios } from "@entrilia/shared/services/downloadFile";
import { api } from "../../api/client";
import { impersonationSelector } from "../../store/state/user/selectors";
import ReportingDateSelect from "./filters/ReportingDateSelect";
import WidgetCardWrapper from "./WidgetCardWrapper";
import WidgetContent from "./WidgetContent";
import WidgetHeader from "./WidgetHeader";
import { useBalanceWidgetData } from "./widgetHooks";

interface Props {
  widgetSettings: PortalWidget;
  withFixedHeight?: boolean;
}

const exportBalanceReport = withErrorHandling(api.widgets.exportBalanceReport);

const BalanceWidget = ({ widgetSettings, withFixedHeight }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const isImpersonation = useSelector(impersonationSelector);
  const gridApiRef = useGridApiRef();
  const [isExporting, setIsExporting] = useState(false);
  const {
    parameters,
    report,
    fetchError,
    isRunningReport,
    noDataText,
    reportingDate,
    setReportingDate,
    investorIds,
    setInvestorIds,
    fundIds,
    setFundIds,
    searchValue,
    setSearchValue,
    filteredRows,
    clearFilters,
    isAnyFilterApplied,
  } = useBalanceWidgetData(widgetSettings);

  const handleExportClick = async () => {
    if (!widgetSettings.reportId || !reportingDate) {
      return;
    }

    setIsExporting(true);

    const [downloadUrl, error] = await exportBalanceReport({
      reportId: widgetSettings.reportId,
      reportingDate,
      investorIds,
      fundIds,
      widgetName: widgetSettings.name,
    });

    if (error) {
      logError(error, "[BalanceWidget] exportBalanceReport");
      sendNotificationError(error.message);
      return;
    }

    if (isImpersonation) {
      await downloadFileFromUrlViaAxios(downloadUrl, `${widgetSettings.name}.xlsx`);
    } else {
      await downloadFileFromUrl(downloadUrl);
    }

    setIsExporting(false);
  };

  return (
    <WidgetCardWrapper withFixedHeight={withFixedHeight} withFullScreenButton>
      <WidgetHeader
        name={widgetSettings.name}
        actions={
          <Button
            color="secondary"
            variant="outlined"
            loading={isExporting}
            disabled={!report?.rows.length || isRunningReport}
            startIcon={<ExportIcon />}
            sx={{ minWidth: "auto", whiteSpace: "nowrap" }}
            onClick={handleExportClick}
          >
            Export
          </Button>
        }
      >
        <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography color="textSecondary">Effective reporting date:</Typography>
              <ReportingDateSelect
                dates={parameters?.reportingDates ?? []}
                selectedDate={reportingDate ?? ""}
                onDateChange={setReportingDate}
                disabled={!parameters?.reportingDates?.length || isRunningReport}
              />
            </Stack>

            <MultiSelectFilter
              options={parameters?.investors ?? []}
              selectedValues={investorIds}
              onChange={setInvestorIds}
              getOptionValue={(inv) => inv.id}
              getOptionLabel={(inv) => inv.name}
              placeholder="Investors"
              StartIcon={PersonIcon}
              disabled={!parameters?.investors.length}
            />

            <MultiSelectFilter
              options={parameters?.funds ?? []}
              selectedValues={fundIds}
              onChange={setFundIds}
              getOptionValue={(fund) => fund.id}
              getOptionLabel={(fund) => fund.name}
              placeholder="Funds"
              StartIcon={CompaniesIcon}
              disabled={!parameters?.funds.length}
            />

            {isAnyFilterApplied && (
              <Button variant="text" color="primary" disabled={isRunningReport} onClick={clearFilters}>
                Clear all
              </Button>
            )}
          </Stack>

          <Box sx={{ ml: "auto" }}>
            <SearchField
              initialValue={searchValue}
              onSearch={setSearchValue}
              disabled={!report?.rows.length || isRunningReport}
              debounceTimeMs={300}
            />
          </Box>
        </Stack>
      </WidgetHeader>
      <WidgetContent
        report={report}
        rows={filteredRows}
        noDataText={noDataText}
        footnote={widgetSettings.footnote}
        loading={isRunningReport}
        fetchError={fetchError}
        gridApiRef={gridApiRef}
      />
    </WidgetCardWrapper>
  );
};

export default BalanceWidget;
